import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const Footer = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(currentLanguage === 'en' ?'Hello, I would like to inquire about your legal services.' :'Hujambo, ningependa kuuliza kuhusu huduma zenu za kisheria.');
    window.open(`https://wa.me/254721320787?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.open('tel:+254721320787', '_self');
  };

  const handleEmailClick = () => {
    window.open('mailto:info@peterpartnerslaw.co.ke', '_self');
  };

  const practiceAreas = [
    {
      name: currentLanguage === 'en' ? 'Corporate Law' : 'Sheria ya Makampuni',
      path: '/individual-practice-area-detail?area=corporate-law'
    },
    {
      name: currentLanguage === 'en' ? 'Family Law' : 'Sheria ya Familia',
      path: '/individual-practice-area-detail?area=family-law'
    },
    {
      name: currentLanguage === 'en' ? 'Property Law' : 'Sheria ya Mali',
      path: '/individual-practice-area-detail?area=property-law'
    },
    {
      name: currentLanguage === 'en' ? 'Criminal Defense' : 'Utetezi wa Jinai',
      path: '/individual-practice-area-detail?area=criminal-defense'
    }
  ];

  const quickLinks = [
    {
      name: currentLanguage === 'en' ? 'About Us' : 'Kuhusu Sisi',
      path: '/about-us'
    },
    {
      name: currentLanguage === 'en' ? 'Our Team' : 'Timu Yetu',
      path: '/team-profiles'
    },
    {
      name: currentLanguage === 'en' ? 'Contact Us' : 'Wasiliana Nasi',
      path: '/contact-us'
    },
    {
      name: currentLanguage === 'en' ? 'Practice Areas' : 'Maeneo ya Utendaji',
      path: '/practice-areas-overview'
    }
  ];

  const contactInfo = [
    {
      icon: 'MapPin',
      text: currentLanguage === 'en' ? 'Nairobi, Kenya' : 'Nairobi, Kenya',
      action: null
    },
    {
      icon: 'Phone',
      text: '+254 721 320 787',
      action: handleCallClick
    },
    {
      icon: 'Mail',
      text: 'info@peterpartnerslaw.co.ke',
      action: handleEmailClick
    }
  ];

  const content = {
    en: {
      companyDescription: "Peter & Partners Advocates LLP is a leading law firm in Kenya, providing comprehensive legal services with integrity, expertise, and dedication to client success.",
      practiceAreasTitle: "Practice Areas",
      quickLinksTitle: "Quick Links",
      contactTitle: "Contact Info",
      followUsTitle: "Follow Us",
      copyrightText: "All rights reserved. Peter & Partners Advocates LLP",
      legalNotice: "This website is for informational purposes only and does not constitute legal advice."
    },
    sw: {
      companyDescription: "Peter & Partners Advocates LLP ni kampuni ya mawakili inayoongoza Kenya, ikitoa huduma kamili za kisheria kwa uongozi, utaalamu, na kujitolea kwa mafanikio ya wateja.",
      practiceAreasTitle: "Maeneo ya Utendaji",
      quickLinksTitle: "Viungo vya Haraka",
      contactTitle: "Maelezo ya Mawasiliano",
      followUsTitle: "Tufuate",
      copyrightText: "Haki zote zimehifadhiwa. Peter & Partners Advocates LLP",
      legalNotice: "Tovuti hii ni kwa madhumuni ya habari tu na haitoi ushauri wa kisheria."
    }
  };

  const currentContent = content[currentLanguage];
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-text-primary text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/homepage" className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg">
                <Icon name="Scale" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-lg leading-tight">
                  Peter Partners
                </span>
                <span className="font-caption text-xs text-white/80 leading-tight">
                  {currentLanguage === 'en' ? 'Law Firm' : 'Kampuni ya Sheria'}
                </span>
              </div>
            </Link>
            
            <p className="text-white/80 leading-relaxed mb-6">
              {currentContent.companyDescription}
            </p>

            {/* Social Links */}
            <div>
              <h4 className="font-heading font-semibold mb-4">{currentContent.followUsTitle}</h4>
              <div className="flex space-x-4">
                <button
                  onClick={handleWhatsAppClick}
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-primary transition-colors duration-300 focus-ring"
                  aria-label="WhatsApp"
                >
                  <Icon name="MessageCircle" size={20} />
                </button>
                <button
                  onClick={handleCallClick}
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-primary transition-colors duration-300 focus-ring"
                  aria-label="Phone"
                >
                  <Icon name="Phone" size={20} />
                </button>
                <button
                  onClick={handleEmailClick}
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-primary transition-colors duration-300 focus-ring"
                  aria-label="Email"
                >
                  <Icon name="Mail" size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Practice Areas */}
          <div>
            <h4 className="font-heading font-semibold mb-6">{currentContent.practiceAreasTitle}</h4>
            <ul className="space-y-3">
              {practiceAreas.map((area, index) => (
                <li key={index}>
                  <Link
                    to={area.path}
                    className="text-white/80 hover:text-primary transition-colors duration-300 text-sm"
                  >
                    {area.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold mb-6">{currentContent.quickLinksTitle}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-white/80 hover:text-primary transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-semibold mb-6">{currentContent.contactTitle}</h4>
            <ul className="space-y-4">
              {contactInfo.map((info, index) => (
                <li key={index}>
                  {info.action ? (
                    <button
                      onClick={info.action}
                      className="flex items-start space-x-3 text-white/80 hover:text-primary transition-colors duration-300 text-sm text-left"
                    >
                      <Icon name={info.icon} size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{info.text}</span>
                    </button>
                  ) : (
                    <div className="flex items-start space-x-3 text-white/80 text-sm">
                      <Icon name={info.icon} size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{info.text}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* Office Hours */}
            <div className="mt-6 p-4 bg-white/5 rounded-lg">
              <h5 className="font-heading font-semibold mb-3 text-sm">
                {currentLanguage === 'en' ? 'Office Hours' : 'Masaa ya Ofisi'}
              </h5>
              <div className="space-y-2 text-xs text-white/80">
                <div className="flex justify-between">
                  <span>{currentLanguage === 'en' ? 'Mon - Fri' : 'Juma - Iju'}</span>
                  <span>8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>{currentLanguage === 'en' ? 'Saturday' : 'Jumamosi'}</span>
                  <span>9:00 AM - 2:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>{currentLanguage === 'en' ? 'Sunday' : 'Jumapili'}</span>
                  <span>{currentLanguage === 'en' ? 'Emergency' : 'Dharura'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="py-6 border-t border-white/20">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-white/80 text-sm">
                © {currentYear} {currentContent.copyrightText}
              </p>
              <p className="text-white/60 text-xs mt-1">
                {currentContent.legalNotice}
              </p>
            </div>

            <div className="flex items-center space-x-6">
              <Link
                to="/contact-us"
                className="text-white/80 hover:text-primary transition-colors duration-300 text-sm"
              >
                {currentLanguage === 'en' ? 'Privacy Policy' : 'Sera ya Faragha'}
              </Link>
              <Link
                to="/contact-us"
                className="text-white/80 hover:text-primary transition-colors duration-300 text-sm"
              >
                {currentLanguage === 'en' ? 'Terms of Service' : 'Masharti ya Huduma'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;